import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFilterOptions } from "../../../services/StatisticsService";

const EMPTY = {
  years: [], semesters: [], departments: [], levels: [], classes: [],
  groups: [], subjects: [], exams: [], studentStatuses: [],
};

// Filtres partagés par les statistiques, les tableaux de bord et le conseil de
// classe : année universitaire (l'année en cours par défaut), semestre,
// spécialité, niveau, classe, groupe, matière, examen, statut étudiant et
// période. Toutes les options viennent d'un seul appel qui renvoie déjà ce que
// le compte a le droit de voir (un enseignant ne voit que ses classes et ses
// matières). `enabled` à false n'appelle aucune API.
export function useYearFilters(enabled = true) {
  const [options, setOptions] = useState(EMPTY);
  const [scope, setScope] = useState("staff");
  const [yearId, setYearId] = useState("");
  const [semesterId, setSemesterId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [levelId, setLevelId] = useState("");
  const [classId, setClassId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [examId, setExamId] = useState("");
  const [studentStatus, setStudentStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    getFilterOptions()
      .then((res) => {
        const data = res.data?.data || EMPTY;
        setOptions({ ...EMPTY, ...data });
        setScope(data.scope || "staff");
        const current = (data.years || []).find((y) => y.is_current) || (data.years || [])[0];
        setYearId(current?.id || "");
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, [enabled]);

  // Listes dépendantes : chaque liste ne propose que ce qui est compatible
  // avec les choix déjà faits.
  const semesters = useMemo(
    () => options.semesters.filter((s) => !yearId || s.academic_year_id === yearId),
    [options.semesters, yearId],
  );
  const classes = useMemo(
    () => options.classes.filter((c) => (!yearId || c.academic_year_id === yearId)
      && (!departmentId || c.department_id === departmentId)
      && (!levelId || c.level_id === levelId)),
    [options.classes, yearId, departmentId, levelId],
  );
  const groups = useMemo(
    () => options.groups.filter((g) => (classId ? g.class_id === classId : classes.some((c) => c.id === g.class_id))),
    [options.groups, classId, classes],
  );
  const subjects = useMemo(
    () => options.subjects.filter((s) => (!departmentId || s.department_id === departmentId)
      && (!levelId || !s.level_id || s.level_id === levelId)
      && (!semesterId || s.semester_id === semesterId)),
    [options.subjects, departmentId, levelId, semesterId],
  );
  const exams = useMemo(
    () => options.exams.filter((e) => (!yearId || e.academic_year_id === yearId)
      && (!semesterId || !e.semester_id || e.semester_id === semesterId)
      && (!subjectId || e.subject_id === subjectId)),
    [options.exams, yearId, semesterId, subjectId],
  );

  // Une sélection devenue incompatible est effacée.
  useEffect(() => { if (semesterId && !semesters.some((s) => s.id === semesterId)) setSemesterId(""); }, [semesters, semesterId]);
  useEffect(() => { if (classId && !classes.some((c) => c.id === classId)) setClassId(""); }, [classes, classId]);
  useEffect(() => { if (groupId && !groups.some((g) => g.id === groupId)) setGroupId(""); }, [groups, groupId]);
  useEffect(() => { if (subjectId && !subjects.some((s) => s.id === subjectId)) setSubjectId(""); }, [subjects, subjectId]);
  useEffect(() => { if (examId && !exams.some((e) => e.id === examId)) setExamId(""); }, [exams, examId]);

  const params = {
    semester_id: semesterId || undefined,
    department_id: departmentId || undefined,
    level_id: levelId || undefined,
    class_id: classId || undefined,
    group_id: groupId || undefined,
    subject_id: subjectId || undefined,
    exam_id: examId || undefined,
    student_status: studentStatus || undefined,
    from: from || undefined,
    to: to || undefined,
  };

  // Nombre de filtres avancés actifs (hors année / semestre / spécialité / classe).
  const advancedCount = [levelId, groupId, subjectId, examId, studentStatus, from, to].filter(Boolean).length;

  const reset = () => {
    setSemesterId(""); setDepartmentId(""); setLevelId(""); setClassId(""); setGroupId("");
    setSubjectId(""); setExamId(""); setStudentStatus(""); setFrom(""); setTo("");
    const current = options.years.find((y) => y.is_current) || options.years[0];
    setYearId(current?.id || "");
  };

  return {
    ready, scope, advancedCount, reset,
    years: options.years, semesters, departments: options.departments, levels: options.levels,
    classes, groups, subjects, exams, studentStatuses: options.studentStatuses,
    yearId, setYearId,
    semesterId, setSemesterId,
    departmentId, setDepartmentId,
    levelId, setLevelId,
    classId, setClassId,
    groupId, setGroupId,
    subjectId, setSubjectId,
    examId, setExamId,
    studentStatus, setStudentStatus,
    from, setFrom,
    to, setTo,
    // Paramètres de requête des API de statistiques (hors année : l'année
    // est dans l'URL des routes /academic-years/:id).
    params,
    // Idem, avec l'année, pour les routes qui la prennent en paramètre.
    paramsWithYear: { academic_year_id: yearId || undefined, ...params },
    // Clé stable pour déclencher un rechargement quand un filtre change.
    key: JSON.stringify([yearId, params]),
  };
}

const ALL = ["year", "semester", "department", "class", "level", "group", "subject", "exam", "status", "period"];

// `show` : liste des filtres à afficher (par défaut tous). Les filtres
// principaux sont toujours visibles, les autres sont dans « Plus de filtres ».
export const YearFilters = ({ filters, small = false, show = ALL }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selectClass = `form-select${small ? " form-select-sm" : ""} w-auto`;
  const has = (k) => show.includes(k);
  const main = ["year", "semester", "department", "class"].filter(has);
  const more = ["level", "group", "subject", "exam", "status", "period"].filter(has);
  const hideTeacherOnly = filters.scope === "teacher";

  const select = (value, onChange, label, allLabel, items, getLabel) => (
    <select className={selectClass} value={value} aria-label={label} onChange={(e) => onChange(e.target.value)}>
      {allLabel !== null && <option value="">{allLabel}</option>}
      {items.map((it) => <option key={it.id} value={it.id}>{getLabel(it)}</option>)}
    </select>
  );

  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex flex-wrap gap-2 align-items-center">
        {has("year") && select(filters.yearId, filters.setYearId, t("common.fields.academicYear"), null, filters.years, (y) => y.name || y.code)}
        {has("semester") && select(filters.semesterId, filters.setSemesterId, t("pages.subjects.semester"), t("pages.subjects.allSemesters"), filters.semesters, (s) => s.name || s.code)}
        {has("department") && select(filters.departmentId, filters.setDepartmentId, t("common.fields.department"), t("common.fields.allDepartments"), filters.departments, (d) => d.name)}
        {has("class") && select(filters.classId, filters.setClassId, t("common.fields.class"), t("common.fields.allClasses"), filters.classes, (c) => c.name || c.code)}
        {more.length > 0 && (
          <button type="button" className={`btn ${small ? "btn-sm " : ""}btn-outline-secondary`} onClick={() => setOpen(!open)}>
            <i className="ti ti-filter me-1"></i>{t("pages.filters.more")}
            {filters.advancedCount > 0 && <span className="badge bg-primary ms-1">{filters.advancedCount}</span>}
          </button>
        )}
        <button type="button" className={`btn ${small ? "btn-sm " : ""}btn-link`} onClick={filters.reset}>
          {t("pages.filters.reset")}
        </button>
      </div>
      {open && (
        <div className="d-flex flex-wrap gap-2 align-items-center">
          {has("level") && !hideTeacherOnly && select(filters.levelId, filters.setLevelId, t("common.fields.level"), t("common.fields.allLevels"), filters.levels, (l) => l.name || l.code)}
          {has("level") && hideTeacherOnly && filters.levels.length > 1 && select(filters.levelId, filters.setLevelId, t("common.fields.level"), t("common.fields.allLevels"), filters.levels, (l) => l.name || l.code)}
          {has("group") && select(filters.groupId, filters.setGroupId, t("pages.filters.group"), t("pages.filters.allGroups"), filters.groups, (g) => g.name)}
          {has("subject") && select(filters.subjectId, filters.setSubjectId, t("common.fields.subject"), t("common.fields.allSubjects"), filters.subjects, (s) => s.name || s.code)}
          {has("exam") && select(filters.examId, filters.setExamId, t("pages.filters.exam"), t("pages.filters.allExams"), filters.exams, (e) => e.title)}
          {has("status") && (
            <select className={selectClass} value={filters.studentStatus} aria-label={t("pages.filters.studentStatus")} onChange={(e) => filters.setStudentStatus(e.target.value)}>
              <option value="">{t("pages.filters.allStatuses")}</option>
              {filters.studentStatuses.map((s) => <option key={s} value={s}>{t(`pages.filters.status.${s}`)}</option>)}
            </select>
          )}
          {has("period") && (
            <>
              <input type="date" className={`form-control${small ? " form-control-sm" : ""} w-auto`} value={filters.from} max={filters.to || undefined}
                aria-label={t("pages.filters.from")} onChange={(e) => filters.setFrom(e.target.value)} />
              <input type="date" className={`form-control${small ? " form-control-sm" : ""} w-auto`} value={filters.to} min={filters.from || undefined}
                aria-label={t("pages.filters.to")} onChange={(e) => filters.setTo(e.target.value)} />
            </>
          )}
        </div>
      )}
    </div>
  );
};